import { cva, type VariantProps } from "class-variance-authority"
import React from "react"
import { Card, CardContent, CardTitle } from "@/components/ui/card"
import { cn } from "@/utils/cn"

const widgetCardContainerVariants = cva(
  "relative flex flex-col overflow-hidden rounded-2xl bg-background px-5 py-4",
)

const widgetCardContentVariants = cva("relative flex grow flex-col")

const widgetCardTintVariants = cva("absolute inset-0 bg-linear-to-bl", {
  variants: {
    tint: {
      none: "from-foreground/[0.04] via-background to-background dark:via-foreground/[0.04]",
      success: "from-success/10 via-background to-background dark:via-success/5",
      destructive: "from-destructive/10 via-background to-background dark:via-destructive/5",
      warning: "from-warning/10 via-background to-background dark:via-warning/5",
      primary: "from-purple-500/10 via-background to-background dark:via-purple-500/5",
    },
  },
  defaultVariants: {
    tint: "none",
  },
})

const WidgetCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof widgetCardContainerVariants> &
    VariantProps<typeof widgetCardTintVariants> & {
      containerClassName?: string
    }
>(({ containerClassName, className, tint, children, ...props }, ref) => (
  <Card ref={ref} className={cn(widgetCardContainerVariants(), containerClassName)} {...props}>
    <div className={widgetCardTintVariants({ tint })} />
    <div className={cn(widgetCardContentVariants(), className)}>{children}</div>
  </Card>
))
WidgetCard.displayName = "WidgetCard"

const WidgetCardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex w-full @2xl:flex-row flex-col items-center justify-between gap-x-4 gap-y-3 whitespace-nowrap pb-5",
        className,
      )}
      {...props}
    />
  ),
)
WidgetCardHeader.displayName = "WidgetCardHeader"

const WidgetCardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <CardTitle
      ref={ref}
      className={cn(
        "flex flex-row items-center gap-1.5 font-display font-semibold text-foreground text-lg",
        className,
      )}
      {...props}
    />
  ),
)
WidgetCardTitle.displayName = "WidgetCardTitle"

const WidgetCardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <CardContent ref={ref} className={cn("p-0", className)} {...props} />
  ),
)
WidgetCardContent.displayName = "WidgetCardContent"

export { WidgetCard, WidgetCardHeader, WidgetCardTitle, WidgetCardContent }
